/**
 * FmNavComponents — Catalog-driven nav primitives (7usro.4)
 *
 * FmNavGroup, FmNavItem, FmNavPromoCard, FmMobileNavDrawer
 * These are DS primitives that compose into mega-menus and mobile drawers.
 *
 * No hardcoded link lists; all nav derives from the typed catalog.
 * Semantic props only (tone, variant, density, emphasis); no layout tokens.
 *
 * Doc: frontend/docs/design-system/gap-analysis/07-product-nav-agent-docs-amendment.md §4
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useFmTopBarMeasuredStyle } from './FmTopBarMeasuredHeightContext';
import type {
  FmRouteCatalogEntry,
  FmRouteCatalogData,
  FmTopNavJobEntry,
  PrimaryNavId,
} from './FmRouteCatalog';
import { getGroupRoutes, getJobGroups } from './FmRouteCatalog';
import { FmAccordion } from '@/components/ui/FmAccordion';
import './FmNavComponents.css';

/**
 * FmNavItem — Single route link for mega-menu or mobile drawer.
 * Semantic props: no inline layout/spacing tokens.
 */
export interface FmNavItemProps {
  label: string;
  /** Catalog route id, exposed so tests address a destination in any locale. */
  routeId?: string;
  description?: string;
  icon?: React.ReactNode;
  badge?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  active?: boolean;
  onClick?: () => void;
  href?: string;
  /** Localized label for the coming-soon badge (defaults to English). */
  comingSoonLabel?: string;
}

export const FmNavItem: React.FC<FmNavItemProps> = ({
  label,
  routeId,
  description,
  icon,
  badge,
  disabled,
  comingSoon,
  active,
  onClick,
  href,
  comingSoonLabel = 'Coming soon',
}) => {
  const unavailable = Boolean(disabled || comingSoon);
  return (
    <a
      // Coming-soon / disabled items expose NO live href, so cmd/middle-click and
      // hover-preview cannot reach an unbuilt (404) path — the item is a disabled
      // control, not a reachable dead link (c9tzf).
      href={unavailable ? undefined : href}
      onClick={(e) => {
        if (unavailable || !href) {
          e.preventDefault();
          return;
        }
        const isUnmodifiedPrimaryClick =
          e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        if (!isUnmodifiedPrimaryClick) return;
        e.preventDefault();
        onClick?.();
      }}
      className={[
        'fm-nav-item',
        active && 'fm-nav-item--active',
        disabled && 'fm-nav-item--disabled',
        comingSoon && 'fm-nav-item--coming-soon',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`nav-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
      data-nav-route-id={routeId}
      role="menuitem"
      aria-disabled={unavailable || undefined}
      tabIndex={unavailable ? -1 : 0}
    >
      {icon && <span className="fm-nav-item__icon">{icon}</span>}
      <div className="fm-nav-item__content">
        <div className="fm-nav-item__label">{label}</div>
        {description && <div className="fm-nav-item__description">{description}</div>}
      </div>
      {comingSoon && (
        <span className="fm-nav-item__badge fm-nav-item__badge--coming-soon">
          {comingSoonLabel}
        </span>
      )}
      {badge && !comingSoon && <span className="fm-nav-item__badge">{badge}</span>}
    </a>
  );
};

/**
 * FmNavGroup — Group of routes with optional heading/description.
 * Renders routes as FmNavItems.
 */
export interface FmNavGroupProps {
  heading: string;
  description?: string;
  routes: FmRouteCatalogEntry[];
  onRouteClick?: (routeId: string, path: string) => void;
  activeRouteId?: string;
  /** Localized coming-soon badge label, forwarded to each item. */
  comingSoonLabel?: string;
}

export const FmNavGroup: React.FC<FmNavGroupProps> = ({
  heading,
  description,
  routes,
  onRouteClick,
  activeRouteId,
  comingSoonLabel,
}) => (
  <fieldset className="fm-nav-group">
    {heading && <div className="fm-nav-group__heading">{heading}</div>}
    {description && <div className="fm-nav-group__description">{description}</div>}
    <div className="fm-nav-group__items">
      {routes.map((route) => (
        <FmNavItem
          key={route.id}
          routeId={route.id}
          label={route.label}
          description={route.description}
          active={activeRouteId === route.id}
          comingSoon={route.availability === 'coming-soon'}
          comingSoonLabel={comingSoonLabel}
          onClick={() => onRouteClick?.(route.id, route.path)}
          href={route.path}
        />
      ))}
    </div>
  </fieldset>
);

/**
 * FmNavPromoCard — Contextual help/alert/action card for mega-menu.
 * Renders alongside groups; not bespoke JSX, config-driven.
 */
export interface FmNavPromoCardProps {
  heading: string;
  body: string;
  actionLabel: string;
  onAction?: () => void;
  actionHref?: string;
  badge?: string;
  /**
   * When true the promo's target is a coming-soon / unbuilt route: the CTA is
   * rendered disabled + badged so it never navigates to a dead path (c9tzf).
   */
  comingSoon?: boolean;
  /** Localized label for the coming-soon badge (defaults to English). */
  comingSoonLabel?: string;
}

export const FmNavPromoCard: React.FC<FmNavPromoCardProps> = ({
  heading,
  body,
  actionLabel,
  onAction,
  actionHref,
  badge,
  comingSoon,
  comingSoonLabel = 'Coming soon',
}) => {
  // A coming-soon target shows the localized badge (reusing the existing badge
  // style) and disables the CTA — no dead-navigating promo button.
  const effectiveBadge = comingSoon ? comingSoonLabel : badge;
  return (
    <div className="fm-nav-promo-card">
      {effectiveBadge && <div className="fm-nav-promo-card__badge">{effectiveBadge}</div>}
      <h3 className="fm-nav-promo-card__heading">{heading}</h3>
      <p className="fm-nav-promo-card__body">{body}</p>
      <button
        type="button"
        className="fm-nav-promo-card__action"
        onClick={comingSoon ? undefined : onAction}
        disabled={comingSoon}
        aria-disabled={comingSoon || undefined}
        data-testid="promo-card-action"
      >
        {actionLabel}
        {!comingSoon && actionHref && (
          <a href={actionHref} className="fm-nav-promo-card__link" aria-label={actionLabel}>
            {actionLabel}
          </a>
        )}
      </button>
    </div>
  );
};

/**
 * FmMobileNavDrawer — Accordion rendering of catalog routes for mobile.
 * Not a mega-menu; stacks groups vertically with collapse/expand.
 */
export interface FmMobileNavDrawerProps {
  open: boolean;
  onClose?: () => void;
  catalog: FmRouteCatalogData; // Full catalog for resolving routes
  topJobs: FmTopNavJobEntry[];
  resolveRoutePath?: (route: FmRouteCatalogEntry) => string;
  /** Destination resolver for top-level direct owner jobs. */
  resolveDirectJobPath?: (route: FmRouteCatalogEntry) => string;
  onRouteClick?: (routeId: string, path: string) => void;
  propertyLabel?: string | null;
  propertyActionLabel?: string;
  onPropertyActivate?: () => void;
  activeRouteId?: string;
  activePrimaryNavId?: PrimaryNavId;
  /** Localized coming-soon badge label, forwarded to each group. */
  comingSoonLabel?: string;
}

export const FmMobileNavDrawer: React.FC<FmMobileNavDrawerProps> = ({
  open,
  onClose,
  catalog,
  topJobs,
  resolveRoutePath = (route) => route.path,
  resolveDirectJobPath = resolveRoutePath,
  onRouteClick,
  propertyLabel,
  propertyActionLabel,
  onPropertyActivate,
  activeRouteId,
  activePrimaryNavId,
  comingSoonLabel,
}) => {
  const measuredStyle = useFmTopBarMeasuredStyle();
  if (!open) return null;

  // Portal to <body> so the overlay escapes the top bar's stacking context. The
  // drawer is a child of the sticky FmTopBar; rendered in place, its backdrop
  // scrim painted BELOW the full-bleed Leaflet map (which sits in a sibling
  // stacking context), so a backdrop tap hit the map instead of closing the
  // drawer. At body level the drawer's own z-index wins.
  return createPortal(
    <nav
      className="fm-mobile-nav-drawer fm-mobile-nav-drawer--open"
      data-testid="fm-mobile-nav-drawer"
      style={measuredStyle}
    >
      <button
        type="button"
        className="fm-mobile-nav-drawer__backdrop fm-drawer__backdrop"
        data-testid="fm-mobile-nav-backdrop"
        aria-label={propertyActionLabel ?? 'Close navigation'}
        onClick={onClose}
      />
      <div className="fm-mobile-nav-drawer__panel">
        {propertyLabel && onPropertyActivate && (
          <button
            type="button"
            className="fm-mobile-nav-drawer__property"
            onClick={onPropertyActivate}
            data-testid="nav-drawer-property-switch"
          >
            <span className="fm-mobile-nav-drawer__property-label">{propertyLabel}</span>
            {propertyActionLabel && (
              <span className="fm-mobile-nav-drawer__property-action">{propertyActionLabel}</span>
            )}
          </button>
        )}
        {topJobs.map((job) => {
          if (job.activation.kind === 'route') {
            const routeId = job.activation.routeId;
            const route = catalog.routes.find((candidate) => candidate.id === routeId);
            if (!route || route.availability === 'coming-soon') return null;
            const path = resolveDirectJobPath(route);
            return (
              <button
                key={job.id}
                type="button"
                className={[
                  'fm-mobile-nav-drawer__job-action',
                  activePrimaryNavId === job.id && 'fm-mobile-nav-drawer__job-action--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={activePrimaryNavId === job.id ? 'page' : undefined}
                data-testid={`nav-drawer-job-${job.id}`}
                onClick={() => onRouteClick?.(route.id, path)}
              >
                {/* Holds the caret column so a direct job's label lines up
                    with the accordion jobs around it. */}
                <span className="fm-mobile-nav-drawer__job-action-lead" aria-hidden="true" />
                <span className="fm-mobile-nav-drawer__job-action-label">{job.label}</span>
                <ArrowRightOutlined className="fm-mobile-nav-drawer__job-action-go" aria-hidden />
              </button>
            );
          }

          // Menu jobs resolve their visible routes from the same catalog used
          // by desktop. Contextual-only routes never appear here.
          const visibleGroups = getJobGroups(catalog, job.id)
            .map((group) => ({
              group,
              routes: getGroupRoutes(catalog, group.id).map((route) => ({
                ...route,
                path: resolveRoutePath(route),
              })),
            }))
            .filter((g) => g.routes.length > 0);

          if (visibleGroups.length === 0) return null;

          return (
            <FmAccordion
              key={job.id}
              plain
              className="fm-mobile-nav-drawer__job"
              items={[
                {
                  key: job.id,
                  label: (
                    <span
                      className={[
                        'fm-mobile-nav-drawer__job-label',
                        activePrimaryNavId === job.id && 'fm-mobile-nav-drawer__job-label--active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      data-testid={`nav-drawer-job-${job.id}`}
                      aria-current={activePrimaryNavId === job.id ? 'page' : undefined}
                    >
                      {job.label}
                    </span>
                  ),
                  children: (
                    <div className="fm-mobile-nav-drawer__job-content">
                      {/* A job with one group is already named by its row;
                          repeating the group heading under it reads twice. */}
                      {visibleGroups.map(({ group, routes }) => (
                        <FmNavGroup
                          key={group.id}
                          heading={visibleGroups.length > 1 ? group.label : ''}
                          description={visibleGroups.length > 1 ? group.description : undefined}
                          routes={routes}
                          onRouteClick={onRouteClick}
                          activeRouteId={activeRouteId}
                          comingSoonLabel={comingSoonLabel}
                        />
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          );
        })}
      </div>
    </nav>,
    document.body,
  );
};
